import { useState, useEffect } from "react";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import PositionSummary from "@/components/PositionSummary";
import { BookedCurveChart, OpenPnlLine, fmtDay } from "@/components/BookedCurve";
import { Button } from "@/components/ui/button";
import Money from "@/components/Money";
import { pfDisplay, symbolDisplay, duplicateBadge, sortStrats, zeroNote } from "@/lib/stratTable";
import { toast } from "sonner";
import {
  BarChart2, TrendingUp, TrendingDown, RefreshCw, Loader2,
  Globe, Activity, AlertTriangle, Target, Calendar, HeartPulse, Download,
} from "lucide-react";

const API = (p) => `/api${p}`;

// RU-UIMOBILE UM-7: global markets in the backend's GLOBAL_TICKERS order, not request-completion order.
export const GLOBAL_ORDER = [
  "S&P 500", "Nasdaq", "Nikkei 225", "Hang Seng", "FTSE 100", "SGX Nifty", "Dollar Index", "US 10Y Bond",
];

export function orderedGlobal(global) {
  const rank = (n) => { const i = GLOBAL_ORDER.indexOf(n); return i === -1 ? GLOBAL_ORDER.length : i; };
  return Object.entries(global || {}).sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
}

// UM-7: "as of" time of the cached market-intel response, in IST. Null when ts is missing or unparsable.
export function asOfIst(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
  const day = d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short" });
  return `${time} IST, ${day}`;
}

function StatBox({ label, value, sub, color = "text-white", testid }) {
  // UM-3: min-w-0 + wrap so a long value never crosses the card border on a phone.
  return (
    <div data-testid={testid} data-fit-box className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 sm:p-4 min-w-0">
      <div className="overline text-zinc-500 mb-1 [overflow-wrap:anywhere]">{label}</div>
      <div className={`text-base sm:text-xl font-mono-data font-bold leading-tight [overflow-wrap:anywhere] ${color}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-zinc-600 mt-0.5 [overflow-wrap:anywhere]">{sub}</div>}
    </div>
  );
}

function VIXGauge({ vix }) {
  if (!vix) return <div className="text-zinc-600 text-sm">VIX unavailable</div>;
  const color =
    vix.regime === "FEAR"        ? "text-red-400" :
    vix.regime === "CAUTION"     ? "text-yellow-400" :
    vix.regime === "NEUTRAL"     ? "text-zinc-300" : "text-green-400";
  return (
    <div className="flex items-center gap-3">
      <div className={`text-3xl font-mono-data font-bold ${color}`}>{vix.value}</div>
      <div>
        <div className={`text-xs font-section ${color}`}>{vix.regime}</div>
        <div className="text-xs text-zinc-500">
          {vix.change_pct >= 0 ? "+" : ""}{vix.change_pct}% today
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [taxYear,  setTaxYear]  = useState(new Date().getFullYear());
  const [equity,   setEquity]   = useState(null);
  const [equityErr, setEquityErr] = useState(false);
  const [strats,   setStrats]   = useState([]);
  const [symbols,  setSymbols]  = useState([]);
  const [intel,    setIntel]    = useState(null);
  const [summary,  setSummary]  = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [days,     setDays]     = useState(30);
  // RU-ANLFIX AF-11: table order. P&L with dir null = the API order (P&L high → low).
  const [stratSort, setStratSort] = useState({ key: "pnl", dir: null });

  async function fetchAll() {
    setLoading(true);
    try {
      const [eqR, strR, symR, intR, sumR, calR] = await Promise.all([
        fetch(API(`/analytics/booked-curve?days=${days}`), { credentials: "include" }),
        fetch(API(`/analytics/strategies?days=${days}`),  { credentials: "include" }),
        fetch(API(`/analytics/symbols?days=${days}`),     { credentials: "include" }),
        fetch(API("/analytics/market-intel"),             { credentials: "include" }),
        fetch(API("/analytics/summary"),                  { credentials: "include" }),
        fetch(API(`/analytics/calendar?days=${days}`),    { credentials: "include" }),
      ]);
      // A failed booked-curve read is shown as an error, never as "no booked trades" (and no stale curve).
      if (eqR.ok) { setEquity(await eqR.json()); setEquityErr(false); }
      else        { setEquity(null); setEquityErr(true); }
      if (strR.ok) setStrats((await strR.json()).items || []);
      if (symR.ok) setSymbols((await symR.json()).items || []);
      if (intR.ok) setIntel(await intR.json());
      if (sumR.ok) setSummary(await sumR.json());
      if (calR.ok) setCalendar(await calR.json());
    } catch (e) {
      setEquity(null);
      setEquityErr(true);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchAll(); }, [days]);

  const stats = equity?.stats || {};
  const fii   = intel?.fii_dii?.latest;
  const vix   = intel?.vix;

  return (
    <AppShell>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-8">
        <PageHeader
          title="Analytics"
          subtitle="Equity curve, performance attribution, market intelligence"
          icon={<BarChart2 className="w-5 h-5" />}
        >
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 text-xs font-section rounded-sm transition-colors ${
                  days === d ? "bg-white text-black" : "text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800"
                }`}
              >
                {d}D
              </button>
            ))}
            <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} className="border-zinc-700">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </PageHeader>

        {/* ── Position Summary (RU-POSSUMMARY) ── */}
        <PositionSummary />

        {loading ? (
          <div className="flex items-center justify-center h-48 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading analytics…
          </div>
        ) : (
          <>
            {/* ── Equity Curve ── */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-zinc-400" />
                <span className="font-section text-sm text-zinc-300">Booked P&L curve ({days}D, ₹)</span>
              </div>
              {equityErr ? (
                <div data-testid="booked-error"
                     className="bg-zinc-900 border border-red-900/60 rounded-sm p-8 text-center text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                  Booked P&L curve could not be loaded. Try refresh.
                </div>
              ) : stats.trading_days > 0 ? (
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                  <BookedCurveChart points={equity.points} />
                  {/* RU-EQUITYRS ER-6 tiles in RU-UIMOBILE's grid / StatBox (UM-3); amounts via Money (UM-1/UM-2). */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4">
                    <StatBox testid="an-stat-booked" label={`Booked P&L (${days}D)`}
                      value={<Money value={stats.booked_pnl} plus testid="an-booked-pnl" />}
                      sub={`${stats.closed_trades} closed trades`}
                      color={stats.booked_pnl >= 0 ? "text-green-400" : "text-red-400"} />
                    <StatBox testid="an-stat-drawdown" label="Max drawdown"
                      value={<Money value={stats.max_drawdown?.amount > 0 ? -stats.max_drawdown.amount : 0}
                                    testid="an-drawdown" />}
                      color={stats.max_drawdown?.amount > 0 ? "text-red-400" : "text-zinc-300"}
                      sub={stats.max_drawdown?.amount > 0
                        ? `${fmtDay(stats.max_drawdown.from)} – ${fmtDay(stats.max_drawdown.to)}`
                        : "No fall below a peak"} />
                    <StatBox testid="an-stat-worstday" label="Worst day"
                      value={stats.worst_day ? <Money value={stats.worst_day.pnl} plus testid="an-worst-day" /> : "—"}
                      sub={stats.worst_day ? fmtDay(stats.worst_day.date) : null}
                      color={stats.worst_day?.pnl < 0 ? "text-red-400" : "text-zinc-300"} />
                    {/* UM-4: counts days with a positive daily P&L, not trades. */}
                    <StatBox testid="an-stat-windays" label="Win days"
                      value={`${stats.win_days}W / ${stats.loss_days}L days`}
                      sub={stats.flat_days > 0 ? `${stats.flat_days} flat` : null}
                      color={stats.win_days >= stats.loss_days ? "text-green-400" : "text-red-400"} />
                  </div>
                  <OpenPnlLine open={equity.open} since={equity.since} />
                </div>
              ) : (
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-8 text-center text-zinc-600 text-sm">
                  No booked (closed) paper trades in the last {days} days.
                  {equity?.open && <OpenPnlLine open={equity.open} since={null} />}
                </div>
              )}
            </section>

            {/* ── Strategy Performance ── */}
            {strats.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-zinc-400" />
                  <span className="font-section text-sm text-zinc-300">Strategy Performance ({days}D)</span>
                </div>
                <div data-testid="an-strat-scroll" className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-x-auto">
                  <table className="w-full min-w-[640px] text-xs">
                    <thead className="border-b border-zinc-800">
                      <tr className="text-zinc-500">
                        {["Strategy", "Symbol", "Mode", "Trades", "Win%", "P&L", "Prof. Factor"].map((h) => {
                          const key = h === "P&L" ? "pnl" : h === "Prof. Factor" ? "pf" : null;
                          if (!key) {
                            return <th key={h} className="px-3 sm:px-4 py-2.5 text-left overline whitespace-nowrap">{h}</th>;
                          }
                          const dir = stratSort.key === key ? (stratSort.dir || (key === "pnl" ? "desc" : null)) : null;
                          const next = dir === "desc" ? "asc" : "desc";
                          return (
                            <th key={h} aria-sort={dir === "desc" ? "descending" : dir === "asc" ? "ascending" : "none"}
                                className="px-3 sm:px-4 py-2.5 text-left overline whitespace-nowrap">
                              <button type="button" data-testid={`an-strat-sort-${key}`}
                                      onClick={() => setStratSort({ key, dir: next })}
                                      className="overline hover:text-zinc-300 inline-flex items-center gap-1">
                                {h}<span aria-hidden="true">{dir === "desc" ? "↓" : dir === "asc" ? "↑" : "↕"}</span>
                              </button>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800/50">
                      {sortStrats(strats, stratSort.key, stratSort.dir).map((s) => {
                        // RU-ANLFIX AF-2 / AF-4 / AF-7 / AF-10 / AF-12.
                        const pf = pfDisplay(s);
                        const zero = zeroNote(s);
                        const sym = symbolDisplay(s);
                        const dup = duplicateBadge(s);
                        return (
                        <tr key={s.id} data-testid={`an-strat-row-${s.id}`} className={`hover:bg-zinc-800/30 transition-colors ${s.unattributed ? "italic" : ""}`}>
                          <td className="px-3 sm:px-4 py-2.5 text-zinc-200 font-medium max-w-[180px]">
                            <div className="truncate max-w-[180px]" title={s.name}>{s.name}</div>
                            {dup && (
                              <span data-testid={`an-strat-dup-${s.id}`} title={dup.title}
                                    className="inline-block mt-0.5 text-[10px] font-section px-1.5 py-px rounded bg-amber-500/15 text-amber-300 whitespace-nowrap not-italic">
                                {dup.text}<span className="sr-only">. {dup.title}</span>
                              </span>
                            )}
                          </td>
                          <td className="px-3 sm:px-4 py-2.5 text-zinc-400 font-mono-data">
                            {/* AF-4: wraps rather than truncates, so "A +N" is always read whole. */}
                            <div data-testid={`an-strat-sym-${s.id}`} className="max-w-[13rem] [overflow-wrap:anywhere]" title={sym.title}>{sym.text}</div>
                            {sym.configured && (
                              <div data-testid={`an-strat-symcfg-${s.id}`} className="max-w-[13rem] text-[10px] text-zinc-600 truncate"
                                   title={`Configured symbol: ${sym.configured}`}>configured: {sym.configured}</div>
                            )}
                          </td>
                          <td className="px-3 sm:px-4 py-2.5">
                            <span className={`text-xs font-section px-1.5 py-0.5 rounded ${
                              s.mode === "live" ? "bg-green-500/20 text-green-300" : "bg-zinc-700 text-zinc-400"
                            }`}>{s.mode}</span>
                          </td>
                          <td className="px-3 sm:px-4 py-2.5 text-zinc-300 font-mono-data whitespace-nowrap">
                            {s.total_trades}
                            {zero && (
                              <div data-testid={`an-strat-zero-${s.id}`} title={zero.title}
                                   className="text-[10px] text-zinc-500">{zero.text}</div>
                            )}
                          </td>
                          <td className={`px-3 sm:px-4 py-2.5 font-mono-data whitespace-nowrap ${s.win_rate_pct >= 50 ? "text-green-400" : "text-red-400"}`}>
                            {s.win_rate_pct}%
                          </td>
                          <td className={`px-3 sm:px-4 py-2.5 font-mono-data font-bold whitespace-nowrap ${s.total_pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                            {/* RU-UIFU UF-5: up to ~874 rows — not a tab stop per row; exact value for screen readers / hover. */}
                            <Money value={s.total_pnl} plus interactive={false} testid={`an-strat-pnl-${s.id}`}
                                   className="[overflow-wrap:normal]" />
                          </td>
                          <td data-testid={`an-strat-pf-${s.id}`} title={pf.title || undefined} className={`px-3 sm:px-4 py-2.5 font-mono-data whitespace-nowrap ${
                            pf.tone === "good" ? "text-green-300" : pf.tone === "bad" ? "text-red-300" : "text-zinc-500"}`}>
                            {pf.text}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* ── Symbol Attribution ── */}
            {symbols.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <BarChart2 className="w-4 h-4 text-zinc-400" />
                  <span className="font-section text-sm text-zinc-300">P&L by Symbol ({days}D)</span>
                </div>
                {/* UM-5: 1 column on phones; the P&L never shrinks, the symbol side truncates. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {symbols.slice(0, 9).map((s) => (
                    <div key={s.symbol} data-testid={`an-sym-${s.symbol}`} data-fit-box className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 flex items-center justify-between gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="font-mono-data text-sm text-white truncate" title={s.symbol}>{s.symbol}</div>
                        <div className="text-xs text-zinc-500 truncate">{s.total_trades} trades · {s.win_rate_pct}% WR</div>
                      </div>
                      <div className={`shrink-0 font-mono-data font-bold text-sm ${s.total_pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                        <Money value={s.total_pnl} plus testid={`an-sym-pnl-${s.symbol}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Market Intelligence ── */}
            <section>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
                <Globe className="w-4 h-4 text-zinc-400" />
                <span className="font-section text-sm text-zinc-300">Market Intelligence</span>
                {asOfIst(intel?.ts) && (
                  <span data-testid="an-intel-asof" className="text-[11px] text-zinc-600 font-mono-data">as of {asOfIst(intel.ts)}</span>
                )}
                <button
                  onClick={() => fetch(API("/analytics/market-intel?refresh=true"), { credentials: "include" })
                    .then((r) => r.json()).then(setIntel)}
                  className="ml-auto text-xs text-zinc-600 hover:text-zinc-400 flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* India VIX */}
                <div data-testid="an-intel-vix" data-fit-box className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 min-w-0">
                  <div className="overline text-zinc-500 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" /> India VIX
                  </div>
                  <VIXGauge vix={vix} />
                  {vix?.history && (
                    <div className="mt-3 flex gap-1">
                      {vix.history.map((h) => (
                        <div key={h.date} className="flex-1 min-w-0 text-center">
                          <div className="text-xs font-mono-data text-zinc-400">{h.value}</div>
                          <div className="text-[11px] text-zinc-500 whitespace-nowrap">{h.date.slice(5)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* FII/DII */}
                <div data-testid="an-intel-fii" data-fit-box className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 min-w-0">
                  <div className="overline text-zinc-500 mb-2">FII / DII Activity</div>
                  {fii ? (
                    <>
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-zinc-400">FII Net</span>
                          <span className={`font-mono-data font-bold ${fii.fii_net == null ? "text-zinc-500" : fii.fii_net >= 0 ? "text-green-400" : "text-red-400"}`}>
                            {fii.fii_net == null ? "—" : `${fii.fii_net >= 0 ? "+" : "-"}₹${Math.abs(fii.fii_net).toLocaleString("en-IN", { maximumFractionDigits: 0 })} Cr`}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-zinc-400">DII Net</span>
                          <span className={`font-mono-data font-bold ${fii.dii_net == null ? "text-zinc-500" : fii.dii_net >= 0 ? "text-green-400" : "text-red-400"}`}>
                            {fii.dii_net == null ? "—" : `${fii.dii_net >= 0 ? "+" : "-"}₹${Math.abs(fii.dii_net).toLocaleString("en-IN", { maximumFractionDigits: 0 })} Cr`}
                          </span>
                        </div>
                        <div className="flex items-center justify-between border-t border-zinc-800 pt-2">
                          <span className="text-zinc-500">Sentiment</span>
                          <span className={`font-section text-xs px-2 py-0.5 rounded ${
                            intel.fii_dii.sentiment === "BULLISH" ? "bg-green-500/20 text-green-300" :
                            intel.fii_dii.sentiment === "BEARISH" ? "bg-red-500/20 text-red-300" :
                            "bg-zinc-700 text-zinc-400"
                          }`}>{intel.fii_dii.sentiment}</span>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-zinc-600">{fii.date}</div>
                    </>
                  ) : (
                    <div className="text-zinc-600 text-sm">FII/DII data unavailable</div>
                  )}
                </div>

                {/* Global Markets */}
                <div data-testid="an-intel-global" data-fit-box className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 min-w-0">
                  <div className="overline text-zinc-500 mb-2">Global Markets</div>
                  {intel?.global && Object.keys(intel.global).length > 0 ? (
                    <div className="space-y-1.5">
                      {orderedGlobal(intel.global).slice(0, 6).map(([name, d]) => (
                        <div key={name} data-testid={`an-global-${name}`} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-zinc-400 truncate min-w-0" title={name}>{name}</span>
                          <div className="text-right shrink-0 whitespace-nowrap">
                            <span className="font-mono-data text-zinc-300">{d.price?.toLocaleString()}</span>
                            <span className={`ml-2 font-mono-data ${d.change_pct >= 0 ? "text-green-400" : "text-red-400"}`}>
                              {d.change_pct >= 0 ? "+" : ""}{d.change_pct}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-zinc-600 text-sm">Global data unavailable</div>
                  )}
                </div>
              </div>
            </section>

            {/* ── Calendar Analytics ── */}
            {calendar?.calendar?.some((d) => d.trades > 0) && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-zinc-400" />
                  <span className="font-section text-sm text-zinc-300">Day-of-Week Edge ({days}D)</span>
                  {calendar.best_day && (
                    <span className="ml-auto text-xs text-zinc-500">
                      Best day: <span className="text-green-400 font-section">{calendar.best_day}</span>
                    </span>
                  )}
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                  <div data-testid="an-calendar" data-fit-box className="flex gap-1 sm:gap-3 items-end h-28 min-w-0">
                    {calendar.calendar.map((d) => {
                      const maxAbs = Math.max(...calendar.calendar.map((x) => Math.abs(x.pnl)), 1);
                      const heightPct = d.trades === 0 ? 0 : Math.max(4, Math.abs(d.pnl) / maxAbs * 100);
                      const isPos = d.pnl >= 0;
                      return (
                        <div key={d.day} className="flex-1 min-w-0 flex flex-col items-center gap-1">
                          <div className="text-[10px] sm:text-xs font-mono-data text-zinc-400 whitespace-nowrap">
                            {d.pnl !== 0
                              ? `${isPos ? "+" : ""}₹${Math.abs(d.pnl) >= 1000
                                  ? (d.pnl / 1000).toFixed(1) + "k"
                                  : d.pnl.toFixed(0)}`
                              : "—"}
                          </div>
                          <div className="w-full relative flex items-end justify-center h-16">
                            {d.trades > 0 && (
                              <div
                                className={`w-full rounded-sm transition-all ${isPos ? "bg-green-500/60" : "bg-red-500/60"}`}
                                style={{ height: `${heightPct}%` }}
                              />
                            )}
                          </div>
                          <div className="text-xs font-section text-zinc-500">{d.day}</div>
                          <div className="text-[10px] text-zinc-600">{d.trades ? `${d.win_rate}%WR` : "0 trades"}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {/* ── Strategy Health (Optimizer) ── */}
            {strats.some((s) => s.mode === "live") && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <HeartPulse className="w-4 h-4 text-zinc-400" />
                  <span className="font-section text-sm text-zinc-300">Strategy Health</span>
                  <span className="text-xs text-zinc-600 ml-1">auto-checked every 6h</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {strats.filter((s) => s.mode === "live").map((s) => {
                    const health = s.optimizer_health || "pending";
                    const color =
                      health === "ok"                 ? "text-green-400" :
                      health === "live_slump"         ? "text-yellow-400" :
                      health === "degraded"           ? "text-red-400" :
                      health === "insufficient_data"  ? "text-zinc-500" : "text-zinc-600";
                    const label =
                      health === "ok"                 ? "HEALTHY" :
                      health === "live_slump"         ? "LIVE SLUMP" :
                      health === "degraded"           ? "DEGRADED" :
                      health === "insufficient_data"  ? "NEED MORE TRADES" : "PENDING CHECK";
                    return (
                      <div key={s.id} className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 flex items-center justify-between gap-3 min-w-0">
                        <div className="min-w-0">
                          <div className="text-sm text-zinc-200 font-medium truncate max-w-[200px]">{s.name}</div>
                          <div className="text-xs text-zinc-500">{s.symbol} · live</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-xs font-section ${color}`}>{label}</div>
                          {s.optimizer_win_rate != null && (
                            <div className="text-xs text-zinc-600 font-mono-data">{s.optimizer_win_rate}% WR live</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Tax / P&L Report ── */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Download className="w-4 h-4 text-zinc-400" />
                <span className="font-section text-sm text-zinc-300">Tax / P&L Report</span>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 flex items-center gap-4 flex-wrap">
                <div className="text-xs text-zinc-500 flex-1">
                  Download full trade ledger (all closed round-trips) for STCG/LTCG tax filing.
                  Each row = BUY→SELL pair with entry date, exit date, symbol, qty, prices, gross P&L.
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={taxYear}
                    onChange={(e) => setTaxYear(Number(e.target.value))}
                    className="bg-zinc-800 border border-zinc-700 text-zinc-200 text-xs rounded-sm px-2 py-1.5 font-mono-data"
                  >
                    {[2023, 2024, 2025, 2026].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <a
                    href={`/api/analytics/tax-report?year=${taxYear}&format=csv`}
                    download={`algoforge_trades_${taxYear}.csv`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-section bg-white text-black hover:bg-zinc-200 transition-colors"
                  >
                    <Download className="w-3 h-3" /> DOWNLOAD CSV
                  </a>
                </div>
              </div>
            </section>

            {/* ── Empty state ── */}
            {!strats.length && !symbols.length && !(stats.trading_days > 0) && (
              <div className="text-center py-12 text-zinc-600 text-sm">
                No trade data for the last {days} days.
                Deploy a strategy and start paper trading to see analytics here.
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
